import type { ReactElement } from 'react';
import { Typography } from '@mui/material';
import { ConfirmOptions } from 'material-ui-confirm';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  warning: {
    color: theme.palette.error.main,
    fontSize: theme.typography.h3.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: 1.15,
  },
  reason: {
    whiteSpace: 'pre-wrap',
  },
}));

type ExportProjectValidationWarningProps = {
  message: string;
};

const BROKEN_EXPORT_WARNINGS = [
  'This file will be broken.',
  'Use it only for debugging.',
  'Do not import it as a real project.',
] as const;

/**
 * Large warning copy shown when a JSON export failed validation.
 */
const ExportProjectValidationWarning = ({
  message,
}: ExportProjectValidationWarningProps): ReactElement => {
  const { classes } = useStyles();

  return (
    <div className={classes.root}>
      {BROKEN_EXPORT_WARNINGS.map((warning) => (
        <Typography key={warning} className={classes.warning} component="p">
          {warning}
        </Typography>
      ))}
      <Typography className={classes.reason} variant="body1">
        {message}
      </Typography>
    </div>
  );
};

/**
 * Confirm options for exporting a project after validation has already failed.
 * Confirm is not focused, so Enter dismisses the dialog.
 */
export const getSkipProjectExportValidationConfirmOptions = (
  error: Error,
): ConfirmOptions => ({
  title: 'WARNING: broken project export',
  titleProps: { color: 'error', variant: 'h4' },
  content: <ExportProjectValidationWarning message={error.message} />,
  confirmationText: 'Export without validation',
  cancellationText: 'Cancel',
  confirmationButtonProps: {
    color: 'error',
    autoFocus: false,
  },
  cancellationButtonProps: {
    autoFocus: true,
  },
  dialogProps: { maxWidth: 'sm', fullWidth: true },
});
